import * as React from "react";
import FormContext, { initFormStore, updateFormValues } from "@/contexts/Form";
import classNames from "classnames";
import { range } from "@/utils/number";
import Article from "@/components/molecules/Article";
import FormBlock from "@/components/molecules/FormBlock";
import FormContainer from "@/components/atoms/FormContainer";
import { IConnectingDirection } from "@/constants/ConnectingDirection";
import { IConnectingType } from "@/constants/ConnectingType";
import form, { ConnectingDetail, Connecting } from "@/utils/form";
import {
  switchConnectingRoadStatusNumber,
  IConnectingStatus,
  connectingStatusOptions,
} from "@/constants/ConnectingStatus";
import { validatePropertyDetail as validator, validateConnecting as connectingValidator } from "@/utils/validation";
import { useFormHandler } from "@/utils/hooks";
import { isBlank } from "@/utils/is";
// FIXME: eslint fix かけても問題なくなったら object-curly-newline を修正する
// eslint-disable-next-line object-curly-newline
import contract, { HIDDEN_COMPONENT_OPTIONS, isHiddenComponent, Feature } from "@/utils/contract";
import ContractContext, { initContractStore } from "@/contexts/Contract";
import { useTheme } from "@/hooks/useTheme";
import ConnectingDetails from "./connectingDetails";
import RoadConnectingStatus from "./roadConnectingStatus";
import MediaQueryContext, { initMediaQueryStore } from "@/contexts/MediaQuery";

const Land = () => {
  const { COLOR_THEME, isNewTheme } = useTheme();
  const {
    state: { values, validates },
    dispatch,
  } = React.useContext(FormContext) || initFormStore(form);
  const { current } = React.useContext(MediaQueryContext) || initMediaQueryStore("md");

  const {
    state: { featuresObj },
  } = React.useContext(ContractContext) || initContractStore(contract);
  const isConnectingRoadInfoAutoEnabled = featuresObj[Feature.CONNECTING_ROAD_INFO_AUTO]?.enabled;
  const handleLandSize = useFormHandler("landSize", validator.landSize);

  // 接道状況
  const [connecting, setConnecting] = React.useState<Connecting>({
    status: "",
    details: [],
  });
  const [validateConnectingStatus, setValidateConnectingStatus] = React.useState("");
  const [validateConnectingDetails, setValidateConnectingDetails] = React.useState<ConnectingDetail[]>([]);

  const handleChangeConnectingStatus = (ev: React.ChangeEvent<HTMLSelectElement>) => {
    const value = ev.target.value as IConnectingStatus;
    const validateMessage = connectingValidator.status(value);
    const newLength = switchConnectingRoadStatusNumber(value);
    const newDetails = range(0, newLength).map(
      (): ConnectingDetail => ({
        type: "" as IConnectingType,
        direction: "" as IConnectingDirection,
        [isNewTheme === true ? "widthGroup" : "width"]: "",
        [isNewTheme === true ? "contactWidthGroup" : "contactWidth"]: "",
      })
    );
    const newState = [...connecting.details, ...newDetails].slice(0, newLength);
    setConnecting(() => ({
      status: value,
      details: newState,
    }));
    const newValidateState = [...validateConnectingDetails, ...newDetails].slice(0, newLength);
    setValidateConnectingStatus(validateMessage);
    setValidateConnectingDetails(newValidateState);
  };

  const hiddenLivingAddressComponent = isHiddenComponent(featuresObj, HIDDEN_COMPONENT_OPTIONS.CUSTOMER_ADDRESS);
  const [isContactStatusOpen, setContactStatusOpen] = React.useState(false);

  /**
   * 売却物件[所在地]部屋番号変更時、
   * [お住いの住所]建物名を更新する
   */
  React.useEffect(() => {
    if (values.isSame && !hiddenLivingAddressComponent) {
      dispatch(
        updateFormValues({
          values: {
            building: `${isBlank(validates.propertyName) ? values.propertyName : ""}${
              isBlank(validates.roomNumber) ? values.roomNumber : ""
            }`,
          },
          validates: {
            building: "",
          },
        })
      );
    }
  }, [values.roomNumber, hiddenLivingAddressComponent]);

  const formBlockProps = {
    className: "form",
    backgroundColor: "#ffffff",
  };

  const isCssOverride = classNames({
    override: isNewTheme,
  });

  const handleInfoIconClick = () => {
    setContactStatusOpen(true);
  };

  const getInfoIconStyles = () => {
    if (isNewTheme && current === "sm") {
      return { width: "100%" };
    } else {
      return {};
    }
  };

  return (
    <Article.Block className="border-none bgWhite">
      <FormContainer className={isCssOverride}>
        <FormBlock {...formBlockProps}>
          <FormBlock.Label
            value="土地面積"
            required
            background={COLOR_THEME.extras?.required_icon?.background}
            text={COLOR_THEME.extras?.required_icon?.text}
            border={COLOR_THEME.extras?.required_icon?.border}
          />
          <FormBlock.Row>
            <FormBlock.Input
              after="㎡"
              placeholder="例）100.0"
              onChange={ev => handleLandSize(ev.target.value)}
              aria-label="土地面積"
              width="200px"
              className="width200px"
              value={values.landSize}
              required
              validateMessage={validates.landSize}
              background={COLOR_THEME.extras?.invalid_state?.background}
            />
          </FormBlock.Row>
        </FormBlock>
      </FormContainer>
      {!isConnectingRoadInfoAutoEnabled && (
        <FormContainer className={isCssOverride}>
          <FormBlock className="underline form" backgroundColor="#ffffff">
            <FormBlock.Label
              value="接道状況"
              required
              style={getInfoIconStyles()}
              background={COLOR_THEME.extras?.required_icon?.background}
              text={COLOR_THEME.extras?.required_icon?.text}
              border={COLOR_THEME.extras?.required_icon?.border}
              modalData="contactStatus"
              onInfoIconClick={handleInfoIconClick}
            />
            <FormBlock.Row>
              <FormBlock.Select
                description=""
                aria-label="接道状況"
                options={connectingStatusOptions}
                width="200px"
                className="width200px"
                onChange={handleChangeConnectingStatus}
                required
                validateMessage={validateConnectingStatus}
                value={connecting.status}
                background={COLOR_THEME.extras?.invalid_state?.background}
              />
            </FormBlock.Row>
          </FormBlock>
          <ConnectingDetails
            isNewTheme={isNewTheme as boolean}
            connecting={connecting}
            validateConnectingDetails={validateConnectingDetails}
            setConnecting={setConnecting}
            setValidateConnectingDetails={setValidateConnectingDetails}
          />
        </FormContainer>
      )}
      <RoadConnectingStatus open={isContactStatusOpen} onCloseClick={() => setContactStatusOpen(false)} />
    </Article.Block>
  );
};

export default Land;
